import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import { OutcomeDot } from "@/components/ui/outcome";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ComboboxList } from "@/components/ui/combobox";
import { CountPicker, Token } from "@/features/cadence-tokens";
import { KindIcon } from "@/features/disposition-icon";
import { endOutcomeLabel, endOutcomeOptions, endOutcomesLeftOut } from "@/lib/cadence-end";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatSpan } from "@/lib/campaign";
import { isCurrentStage, stageSlugs, stageText } from "@/lib/contacts";
import {
  DISPOSITION_CATEGORIES, DISPOSITION_KINDS, DISPOSITION_SLUG_RE, LABEL_MAX, NEW_DISPOSITION,
  categoryTerm, dispositionChanges, dispositionValues, kindMark, kindTerm, minutesProblem, minutesToSeconds,
  newDispositionPayload, nextSortOrder, spanDefaultText, spanField, stageWaitsNote, OUTCOME_GROUPS, defaultOutcomeGroup, groupTerm,
} from "@/lib/dispositions";
import { useFormChanged, zodResolver } from "@/lib/form";
import { useCampaigns, useCreateDisposition, useUpdateDisposition } from "@/lib/queries";
import type { Disposition } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Add an outcome to the catalog, or edit one (ADR-017).
 *
 * The slug is typed once: the contract's `disposition.set`, the CRM's tags and the reports all carry
 * it, so core-api has no way to rename one. The kind is picked from the four the dialer acts on, each
 * saying what it does, and the one span that kind waits on is asked for in minutes, empty for its
 * default. An edit sends only what changed (`dispositionChanges`), so a span saved in odd seconds is
 * never rounded by a label being fixed. Where it sits in the list is moved on the page, not typed
 * here: a new one goes last.
 */

/** Radix's Select cannot hold `""`, so "no category" needs a value of its own. */
const NONE = "none";
/** The Group menu's "from what it does" entry, for a new outcome: sends no group. */
const AUTOMATIC = "__automatic__";

const schema = z
  .object({
    slug: z
      .string()
      .min(1, "Required")
      .regex(DISPOSITION_SLUG_RE, "Lowercase letters, digits, underscore and hyphen, up to 63"),
    label: z
      .string()
      .trim()
      .min(1, "Required")
      .max(LABEL_MAX, `At most ${LABEL_MAX} characters`),
    kind: z.string(),
    category: z.string(),
    backoff_minutes: z.string(),
    hold_minutes: z.string(),
    enlist_campaign_id: z.string(),
    requires_note: z.boolean(),
    stage: z.string(),
    outcome_group: z.string(),
    rep_can_pick: z.boolean(),
    no_show_limit: z.string(),
    no_show_outcome: z.string(),
  })
  .superRefine((values, ctx) => {
    // core-api refuses this pair (ADR-056); the form says why first, on the field that can be
    // changed rather than on the category the admin probably means to keep.
    if (values.category === "compliance" && values.enlist_campaign_id) {
      ctx.addIssue({
        code: "custom",
        path: ["enlist_campaign_id"],
        message: "A compliance outcome takes the lead off every list, so it can add them to none",
      });
    }
    // Only the span the kind waits on is sent, so only that one can hold the save up.
    const span = spanField(values.kind);
    const field = span === "backoff" ? "backoff_minutes" : span === "hold" ? "hold_minutes" : null;
    const problem = field ? minutesProblem(values[field]) : null;
    if (field && problem) ctx.addIssue({ code: "custom", path: [field], message: problem });
    // A no-show limit is a number and an outcome together (ADR-181): half of one would be dropped.
    if (values.kind === "callback" && Boolean(values.no_show_limit) !== Boolean(values.no_show_outcome)) {
      ctx.addIssue({
        code: "custom",
        path: ["no_show_outcome"],
        message: values.no_show_limit
          ? "Pick the outcome to set at the limit, or set no limit"
          : "Pick how many missed bookings, or set nothing",
      });
    }
  });
type FormValues = z.infer<typeof schema>;

export function DispositionFormDialog({
  open, onOpenChange, disposition, tenantId, catalog,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  disposition?: Disposition;
  /** Whose catalog a new one joins. Sent only by a superadmin, who has no tenant of their own. */
  tenantId: number | null;
  /** The catalog it joins, retired rows included, so a new one goes after the last. */
  catalog: readonly Disposition[];
}) {
  const { user: actor } = useAuth();
  const toast = useToast();
  const create = useCreateDisposition();
  const update = useUpdateDisposition();
  // Where an outcome may hand the lead (ADR-056): this tenant's campaigns, which is what core-api
  // will accept. Only fetched while the dialog is open.
  const { data: campaigns } = useCampaigns(
    tenantId ? { tenant_id: tenantId } : {},
    { enabled: open },
  );
  const [formError, setFormError] = React.useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: NEW_DISPOSITION,
  });
  const { errors } = form.formState;
  const changed = useFormChanged(form);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    form.reset(disposition ? dispositionValues(disposition) : NEW_DISPOSITION);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, disposition]);

  const values = form.watch();
  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
    form.setValue(key, value as never, { shouldDirty: true, shouldValidate: true });
  // The group a new outcome starts in when the admin leaves it to its kind, category and stage.
  const automatic = defaultOutcomeGroup({
    slug: values.slug,
    kind: values.kind,
    category: values.category || null,
    stage: values.stage || null,
  });
  const span = spanField(values.kind);

  /** The hint under a span field: what it comes to, or what empty leaves it to. */
  const spanHint = (text: string) => {
    const seconds = minutesProblem(text) === null ? minutesToSeconds(text) : null;
    return seconds
      ? `Minutes. That is ${formatSpan(seconds)}.`
      : `Minutes. ${spanDefaultText(values.kind, disposition?.backoff_seconds ?? null)}`;
  };

  const onSubmit = form.handleSubmit(async (submitted) => {
    setFormError(null);
    try {
      if (disposition) {
        const changes = dispositionChanges(submitted, disposition);
        if (Object.keys(changes).length > 0) {
          await update.mutateAsync({ id: disposition.id, ...changes });
        }
        toast.success("Disposition saved", submitted.label);
      } else {
        await create.mutateAsync({
          ...newDispositionPayload(submitted, nextSortOrder(catalog)),
          ...(actor?.role === "superadmin" && tenantId !== null ? { tenant_id: tenantId } : {}),
        });
        toast.success("Disposition added", submitted.label);
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the disposition.");
    }
  });

  const knownCategory = (DISPOSITION_CATEGORIES as readonly string[]).includes(values.category);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{disposition ? "Edit disposition" : "New disposition"}</DialogTitle>
          <DialogDescription>
            {disposition
              ? "What reps see it called, and what it does to the lead."
              : "An outcome a rep can choose after a call. It goes to the end of the list; move it on the page."}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Label" htmlFor="disposition-label" required error={errors.label?.message}>
                <Input
                  autoComplete="off"
                  placeholder="Referred out"
                  invalid={Boolean(errors.label)}
                  {...form.register("label")}
                />
              </Field>
              <Field
                label="Slug"
                htmlFor="disposition-slug"
                required={!disposition}
                error={errors.slug?.message}
                hint={
                  disposition
                    ? "Fixed. The disposition.set event, the CRM's tags and the reports carry it."
                    : "What the disposition.set event, the CRM's tags and the reports carry. It cannot be changed later."
                }
              >
                <Input
                  // Read-only rather than disabled: a disabled field drops out of the submitted values.
                  readOnly={Boolean(disposition)}
                  className={cn("font-mono", disposition && "bg-surface-sunken text-text-muted")}
                  autoComplete="off"
                  placeholder="referred_out"
                  invalid={Boolean(errors.slug)}
                  {...form.register("slug")}
                />
              </Field>
            </div>

            <fieldset className="min-w-0">
              <legend className="mb-2 text-xs font-medium text-text">What it does to the lead</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {DISPOSITION_KINDS.map((kind) => (
                  <Choice
                    key={kind}
                    name="disposition-kind"
                    checked={values.kind === kind}
                    onSelect={() => set("kind", kind)}
                    title={kindTerm(kind).label}
                    description={kindTerm(kind).description}
                    icon={<KindIcon mark={kindMark(kind)} />}
                  />
                ))}
              </div>
            </fieldset>

            {span === "backoff" && (
              <Field
                key="backoff"
                label={values.kind === "callback" ? "Call back after" : "Try again after"}
                htmlFor="disposition-backoff"
                error={errors.backoff_minutes?.message}
                hint={spanHint(values.backoff_minutes)}
              >
                <Input
                  className="sm:w-40"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="Default"
                  invalid={Boolean(errors.backoff_minutes)}
                  {...form.register("backoff_minutes")}
                />
              </Field>
            )}
            {span === "hold" && (
              <Field
                key="hold"
                label="Hold for"
                htmlFor="disposition-hold"
                error={errors.hold_minutes?.message}
                hint={spanHint(values.hold_minutes)}
              >
                <Input
                  className="sm:w-40"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="Default"
                  invalid={Boolean(errors.hold_minutes)}
                  {...form.register("hold_minutes")}
                />
              </Field>
            )}

            {values.kind === "callback" && (
              <MissedBookings
                limit={values.no_show_limit}
                outcome={values.no_show_outcome}
                self={disposition?.slug ?? values.slug}
                error={errors.no_show_outcome?.message}
                catalog={catalog}
                onLimit={(n) => set("no_show_limit", n === null ? "" : String(n))}
                onOutcome={(slug) => set("no_show_outcome", slug ?? "")}
              />
            )}

            <Field
              label="Category"
              htmlFor="disposition-category"
              hint={categoryTerm(values.category)?.description ?? "How reports group it."}
            >
              <Select
                value={values.category || NONE}
                onValueChange={(v) => set("category", v === NONE ? "" : v)}
              >
                <SelectTrigger id="disposition-category" className="sm:w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {DISPOSITION_CATEGORIES.map((category) => {
                    const term = categoryTerm(category);
                    return (
                      <SelectItem key={category} value={category} description={term?.description}>
                        {term?.label ?? category}
                      </SelectItem>
                    );
                  })}
                  {values.category && !knownCategory && (
                    <SelectItem value={values.category}>{values.category}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label="Stage"
              htmlFor="disposition-stage"
              hint={
                values.category === "compliance"
                  ? "A compliance outcome puts the lead in Do not call whatever this says."
                  : values.stage
                    ? [stageText(values.stage).description, stageWaitsNote(values)].filter(Boolean).join(" ")
                    : "The stage this outcome puts the lead in. Leave it to keep the stage (Team call, Firm question)."
              }
            >
              <Select value={values.stage || NONE} onValueChange={(v) => set("stage", v === NONE ? "" : v)}>
                <SelectTrigger id="disposition-stage" className="sm:w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Keeps the stage</SelectItem>
                  {stageSlugs().map((stage) => (
                    <SelectItem key={stage} value={stage} description={stageText(stage).description}>
                      {stageText(stage).label}
                    </SelectItem>
                  ))}
                  {values.stage && !isCurrentStage(values.stage) && (
                    <SelectItem value={values.stage}>{stageText(values.stage).label}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Group"
              htmlFor="disposition-group"
              hint={`Where a rep finds it after a call, and its colour on every screen. ${
                groupTerm(values.outcome_group || automatic).description
              }`}
            >
              <Select
                value={values.outcome_group || AUTOMATIC}
                onValueChange={(v) => set("outcome_group", v === AUTOMATIC ? "" : v)}
              >
                <SelectTrigger id="disposition-group" className="sm:w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {!disposition && (
                    <SelectItem value={AUTOMATIC} description="Read off its kind, category and stage">
                      <span className="inline-flex items-center gap-2">
                        <OutcomeDot group={automatic} />
                        {groupTerm(automatic).label} (from what it does)
                      </span>
                    </SelectItem>
                  )}
                  {OUTCOME_GROUPS.map((group) => (
                    <SelectItem key={group} value={group} description={groupTerm(group).description}>
                      <span className="inline-flex items-center gap-2">
                        <OutcomeDot group={group} />
                        {groupTerm(group).label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {values.category === "compliance" && (
              <Callout tone="warning">
                A compliance outcome is do-not-call whatever its kind: the dialer never calls the lead
                again, and a call with a lead marks the lead itself do-not-call.
              </Callout>
            )}

            <Field
              label="Add the lead to"
              htmlFor="disposition-enlist"
              error={errors.enlist_campaign_id?.message}
              hint="Choosing this outcome puts the lead on that campaign's dial list, and finishes them on the campaign they were called from. Leave it at None and the outcome only moves the lead within its own campaign."
            >
              <Select
                value={values.enlist_campaign_id || NONE}
                onValueChange={(v) => set("enlist_campaign_id", v === NONE ? "" : v)}
              >
                <SelectTrigger id="disposition-enlist" invalid={Boolean(errors.enlist_campaign_id)}>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {(campaigns ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
              <div>
                <p id="disposition-pick-label" className="text-xs font-medium text-text">
                  Reps can pick it
                </p>
                <p className="text-xs text-text-muted">
                  Off for an outcome the platform records by itself, such as Callback missed: a rep's
                  window leaves it out and a rep cannot record it. A supervisor or an admin still can.
                </p>
              </div>
              <Switch
                checked={values.rep_can_pick}
                onCheckedChange={(checked) => set("rep_can_pick", checked)}
                aria-labelledby="disposition-pick-label"
              />
            </div>

            <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
              <div>
                <p id="disposition-note-label" className="text-xs font-medium text-text">
                  Needs a note
                </p>
                <p className="text-xs text-text-muted">
                  The rep has to write a note to choose it. On a call with a lead, the note goes out
                  with the outcome.
                </p>
              </div>
              <Switch
                checked={values.requires_note}
                onCheckedChange={(checked) => set("requires_note", checked)}
                aria-labelledby="disposition-note-label"
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
              {disposition ? "Save changes" : "Add disposition"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/** "1st", "2nd", "3rd", "4th". */
function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th"}`;
}

/** The no-show limit of an outcome that books a time (ADR-181): after so many of its bookings
 *  missed, another outcome is set for the lead. The outcome picker is the cadence stop card's
 *  (ACL-309), without the outcome being edited: a limit sets another one. */
function MissedBookings({
  limit, outcome, self, error, catalog, onLimit, onOutcome,
}: {
  limit: string;
  outcome: string;
  /** The outcome being edited, by slug. */
  self: string;
  error?: string;
  catalog: readonly Disposition[];
  onLimit: (n: number | null) => void;
  onOutcome: (slug: string | null) => void;
}) {
  const n = limit ? Number(limit) : null;
  const name = outcome ? endOutcomeLabel(outcome, catalog) : null;
  const options = React.useMemo(
    () => endOutcomeOptions(catalog, outcome).filter((o) => o.value !== self),
    [catalog, outcome, self],
  );
  return (
    <div className="space-y-2 rounded-md border border-border px-3 py-2.5">
      <div>
        <p className="text-xs font-medium text-text">Missed bookings</p>
        <p className="text-xs text-text-muted">
          A time booked with this outcome is missed when it passes and nobody reaches the lead: the
          dialer marks it missed, or a no-contact outcome such as Consult no-show closes it. A
          booking moved to another time is not missed.
        </p>
      </div>
      <p className="text-sm leading-7 text-text">
        After{" "}
        <Token
          label="Missed bookings before the limit"
          title="How many missed bookings"
          hint="Counted for each lead since it last arrived in the outcome's stage."
          content={(close) => (
            <CountPicker
              value={n}
              min={1}
              max={10}
              presets={[1, 2, 3, 4, 5]}
              noneLabel="Don't count"
              unit="missed"
              onChange={onLimit}
              close={close}
            />
          )}
        >
          {n === null ? "no limit" : `${n} missed booking${n === 1 ? "" : "s"}`}
        </Token>
        , set{" "}
        <Token
          label="Outcome at the limit"
          title="At the limit"
          hint="Set as the system, with everything it does when a person sets it: the stage, the campaign it hands the lead to, that stage's cadence."
          width="w-96"
          content={(close) => (
            <ComboboxList
              label="Outcomes"
              options={options}
              value={outcome || null}
              clearLabel="Set nothing"
              plainHints
              footer={endOutcomesLeftOut(catalog) > 0 ? "Do-not-call outcomes are not here: a limit never marks a lead do-not-call." : undefined}
              onPick={(slug) => {
                onOutcome(slug);
                close();
              }}
              className="-mx-3 -mb-3 border-t border-border"
              listClassName="max-h-[min(18rem,calc(var(--radix-popover-content-available-height)-13rem))] min-h-24"
            />
          )}
        >
          {name ?? "nothing"}
        </Token>
        .
      </p>
      <p className="text-xs text-text-muted">
        {n !== null && name
          ? `The ${ordinal(n)} missed booking sets ${name} instead of the usual follow-up. The count starts again each time the lead arrives in ${name}.`
          : "Leave it at no limit and missed bookings are not counted."}
      </p>
      {error ? (
        <p role="alert" className="text-xs text-danger-11">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function Choice({
  name, checked, onSelect, title, description, icon,
}: {
  name: string;
  checked: boolean;
  onSelect: () => void;
  title: string;
  description: string;
  icon?: React.ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2">
      <input
        type="radio"
        name={name}
        className="mt-0.5 accent-accent-9"
        checked={checked}
        onChange={onSelect}
      />
      <span>
        <span className="flex items-center gap-1.5 text-xs font-medium text-text">
          {title}
          {icon}
        </span>
        <span className="block text-xs text-text-muted">{description}</span>
      </span>
    </label>
  );
}
